'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuth } from '@/features/auth/hooks'
import { useWidgetConfigSync } from '@/features/dashboard/hooks/use-widget-config-sync'
import {
  addWidget,
  createPage,
  deletePage,
  getPageLayout,
  getPages,
  removeWidget,
  updatePageMeta,
  updateWidget,
} from '@/service/ui-state.api'
import { WIDGET_WRITE_MUTATION_KEY } from '@/service/widget-save-guard'
import {
  COLLAPSIBLE_CONFIG_KEYS,
  type OrderFormCollapsibleConfig,
  orderFormCollapsibleConfigAtomFamily,
  orderFormEntryConditionSignalAtomFamily,
} from '@/state/order'
import { TimeInForce } from '@/types/generated/common/trading'
import {
  type AddWidgetRequest,
  type CreatePageRequest,
  type DeletePageRequest,
  type GetPageLayoutResponse,
  type GetPagesResponse,
  type PageMutationResponse,
  type RemoveWidgetRequest,
  type UpdatePageMetaRequest,
  type UpdateWidgetRequest,
} from '@/types/generated/public_api'
import type { ErrorResponse } from '@/types/service'

export const uiStateKeys = {
  all: (userId?: string | null) => ['uiState', userId] as const,
  pages: (userId?: string | null) => [...uiStateKeys.all(userId), 'pages'] as const,
  pageLayout: (userId?: string | null, pageId?: string) =>
    [...uiStateKeys.all(userId), 'layout', pageId] as const,
}

export const useGetPages = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { user } = useAuth()
  return useQuery<GetPagesResponse, ErrorResponse>({
    queryKey: uiStateKeys.pages(user?.userId),
    queryFn: getPages,
    enabled,
    staleTime: Infinity,
  })
}

export const useGetPageLayout = (
  pageId: string | null,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const { user } = useAuth()
  return useQuery<GetPageLayoutResponse, ErrorResponse>({
    queryKey: uiStateKeys.pageLayout(user?.userId, pageId ?? ''),
    queryFn: () => getPageLayout(pageId!),
    enabled: !!pageId && enabled,
    staleTime: Infinity,
    // Kept for the session: other pages' layouts are preloaded once with no observer,
    // and the default 5-min GC would put the full-page skeleton back on a tab switch.
    gcTime: Infinity,
  })
}

export const useCreatePage = (props?: {
  onSuccess?: (data: PageMutationResponse) => void
  onError?: (error: ErrorResponse) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (req: CreatePageRequest) => createPage(req),
    onSuccess: (data) => {
      // Seed the cache before the invalidate's refetch lands: navigation to the
      // new page is client-side (no server round trip to hide the gap), and page
      // bootstrap bounces off any pageId it can't find in pagesData.
      const page = data.page
      if (page) {
        queryClient.setQueryData<GetPagesResponse>(uiStateKeys.pages(user?.userId), (prev) => {
          if (!prev || prev.pages.some((p) => p.pageId === page.pageId)) return prev
          return { ...prev, pages: [...prev.pages, page] }
        })
      }
      queryClient.invalidateQueries({ queryKey: uiStateKeys.pages(user?.userId) })
      props?.onSuccess?.(data)
    },
    onError: props?.onError,
  })
}

export const useAddWidget = (props?: {
  onSuccess?: (data: PageMutationResponse, variables: AddWidgetRequest) => void
  onError?: (error: ErrorResponse) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (req: AddWidgetRequest) => addWidget(req),
    mutationKey: WIDGET_WRITE_MUTATION_KEY,
    onSuccess: (data, req) => {
      queryClient.setQueryData<GetPageLayoutResponse>(
        uiStateKeys.pageLayout(user?.userId, req.pageId),
        (prev) => {
          if (!prev) return prev
          return {
            ...prev,
            widgets: [
              ...prev.widgets,
              {
                pageId: req.pageId,
                widgetId: req.widgetId,
                widgetType: req.widgetType,
                x: req.x,
                y: req.y,
                w: req.w,
                h: req.h,
                minW: req.minW ?? 2,
                minH: req.minH ?? 2,
                config: req.config,
                updatedAt: new Date().toISOString(),
              },
            ],
          }
        }
      )
      props?.onSuccess?.(data, req)
    },
    onError: props?.onError,
  })
}

export const useUpdateWidget = (props?: {
  onSuccess?: (data: PageMutationResponse, variables: UpdateWidgetRequest) => void
  onError?: (error: ErrorResponse) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (req: UpdateWidgetRequest) => updateWidget(req),
    mutationKey: WIDGET_WRITE_MUTATION_KEY,
    onSuccess: (data, req) => {
      queryClient.setQueryData<GetPageLayoutResponse>(
        uiStateKeys.pageLayout(user?.userId, req.pageId),
        (prev) => {
          if (!prev) return prev
          return {
            ...prev,
            widgets: prev.widgets.map((w) =>
              w.widgetId === req.widgetId
                ? {
                    ...w,
                    x: req.x ?? w.x,
                    y: req.y ?? w.y,
                    w: req.w ?? w.w,
                    h: req.h ?? w.h,
                    // Merge config instead of replace to avoid overwriting
                    // keys managed by separate hooks (e.g. collapsible config)
                    config: req.config ? { ...w.config, ...req.config } : w.config,
                  }
                : w
            ),
          }
        }
      )
      props?.onSuccess?.(data, req)
    },
    onError: props?.onError,
  })
}

export const useRemoveWidget = (props?: {
  onSuccess?: (data: PageMutationResponse, variables: RemoveWidgetRequest) => void
  onError?: (error: ErrorResponse) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (req: RemoveWidgetRequest) => removeWidget(req),
    mutationKey: WIDGET_WRITE_MUTATION_KEY,
    onSuccess: (data, req) => {
      queryClient.setQueryData<GetPageLayoutResponse>(
        uiStateKeys.pageLayout(user?.userId, req.pageId),
        (prev) => {
          if (!prev) return prev
          return {
            ...prev,
            widgets: prev.widgets.filter((w) => w.widgetId !== req.widgetId),
          }
        }
      )
      props?.onSuccess?.(data, req)
    },
    onError: props?.onError,
  })
}

export const useUpdatePageMeta = (props?: {
  onSuccess?: (data: PageMutationResponse) => void
  onError?: (error: ErrorResponse) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (req: UpdatePageMetaRequest) => updatePageMeta(req),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: uiStateKeys.pages(user?.userId) })
      props?.onSuccess?.(data)
    },
    onError: props?.onError,
  })
}

export const useDeletePage = (props?: {
  onSuccess?: (data: PageMutationResponse) => void
  onError?: (error: ErrorResponse) => void
}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return useMutation({
    mutationFn: (req: DeletePageRequest) => deletePage(req),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: uiStateKeys.pages(user?.userId) })
      props?.onSuccess?.(data)
    },
    onError: props?.onError,
  })
}

// ─── Collapsible section config hook ──────────────────────────────────────────
// Manages exit-condition / entry-condition / advanced settings independently
// from react-hook-form state. Persists via widget config (useUpdateWidget).

const COLLAPSIBLE_DEFAULTS: Required<OrderFormCollapsibleConfig> = {
  priceAbove: '',
  priceBelow: '',
  exitConditionEnabled: '',
  entryConditionInstrument: '',
  entryConditionOperator: 'equal_to',
  entryConditionPrice: '',
  entryConditionTriggerType: 'price',
  entryConditionPriceRef: 'mark',
  entryConditionDelaySeconds: '',
  entryConditionExpiryMinutes: '',
  entryConditionEnabled: '',
  entryConditionScheduleVariant: 'delay',
  entryConditionFireAtMs: '',
  entryConditionActionEventType: 'filled',
  entryConditionActionTargetType: 'order_id',
  entryConditionActionTargetValue: '',
  entryConditionActionPartialFillThreshold: '',
  entryConditionLogic: 'single',
  entryConditionGroupJson: '',
  entryConditionTreeJson: '',
  timeInForce: TimeInForce.TIF_GTC,
  expiresValue: '90',
  expiresUnit: 'days',
  reduceOnly: 'false',
  postOnly: 'false',
}

/**
 * Write-only reset used by the order-form Provider when the selected symbol
 * changes. Keeping this separate avoids subscribing that Provider to every
 * character typed into the Entry/Exit condition draft.
 */
export function useResetOrderFormCollapsibleConfig(cardId: string) {
  const setCollapsibleAtom = useSetAtom(orderFormCollapsibleConfigAtomFamily(cardId))
  const { syncConfig } = useWidgetConfigSync()

  return useCallback(() => {
    const next = { ...COLLAPSIBLE_DEFAULTS }
    setCollapsibleAtom(next)

    const flatConfig: Record<string, string> = {}
    COLLAPSIBLE_CONFIG_KEYS.forEach((key) => {
      flatConfig[key] = ''
    })
    syncConfig(cardId, flatConfig)
  }, [cardId, setCollapsibleAtom, syncConfig])
}

export function useOrderFormCollapsibleConfig(cardId: string) {
  const serverConfig = useAtomValue(orderFormCollapsibleConfigAtomFamily(cardId))
  const setCollapsibleAtom = useSetAtom(orderFormCollapsibleConfigAtomFamily(cardId))
  const { syncConfig } = useWidgetConfigSync()

  const [config, setConfig] = useState<Required<OrderFormCollapsibleConfig>>(() => ({
    ...COLLAPSIBLE_DEFAULTS,
    ...serverConfig,
  }))

  // Apply server-restored values once (on first non-null load)
  const serverAppliedRef = useRef(false)
  useEffect(() => {
    if (serverAppliedRef.current || !serverConfig) return
    serverAppliedRef.current = true

    // Safely parse timeInForce in case of legacy string values
    let tif = serverConfig.timeInForce as any
    if (tif === 'gtc') tif = TimeInForce.TIF_GTC
    else if (tif === 'ioc') tif = TimeInForce.TIF_IOC
    else if (tif === 'fok') tif = TimeInForce.TIF_FOK
    else if (tif === 'gtd') tif = TimeInForce.TIF_GTD
    else if (typeof tif === 'string' && !isNaN(Number(tif))) tif = Number(tif)

    setConfig((prev) => ({ ...prev, ...serverConfig, timeInForce: tif }))
  }, [serverConfig])

  // Keep atom in sync whenever local config changes
  useEffect(() => {
    setCollapsibleAtom(config)
  }, [config, setCollapsibleAtom])

  const updateConfig = useCallback(
    (patch: Partial<OrderFormCollapsibleConfig>) => {
      setConfig((prev) => {
        const next = { ...prev, ...patch }

        // Persist only collapsible keys to widget config
        const flatConfig: Record<string, string> = {}
        COLLAPSIBLE_CONFIG_KEYS.forEach((k) => {
          const v = next[k]
          const key = typeof k === 'string' ? k : String(k)
          if (v != null && v !== '') flatConfig[key] = String(v)
        })
        syncConfig(cardId, flatConfig)

        return next
      })
    },
    [cardId, syncConfig]
  )

  // An external caller (e.g. "Add to Orderform" on a History Trigger row —
  // see useHistoryRowInteractions) pushed a patch via
  // orderFormEntryConditionSignalAtomFamily. Routed through updateConfig
  // (not a raw atom read) so it goes through the exact same local-state +
  // persistence path a manual edit would, whether this hook's owning
  // component was already mounted or not.
  const [entryConditionSignal, setEntryConditionSignal] = useAtom(
    orderFormEntryConditionSignalAtomFamily(cardId)
  )
  useEffect(() => {
    if (!entryConditionSignal.patch) return
    updateConfig(entryConditionSignal.patch)
    setEntryConditionSignal((prev) => ({ ...prev, patch: null }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryConditionSignal.nonce])

  const resetConfig = useCallback(() => {
    const next = { ...COLLAPSIBLE_DEFAULTS }
    setConfig(next)
    setCollapsibleAtom(next)

    // Clear server config
    const flatConfig: Record<string, string> = {}
    COLLAPSIBLE_CONFIG_KEYS.forEach((k) => {
      flatConfig[k] = ''
    })
    syncConfig(cardId, flatConfig)
  }, [cardId, syncConfig, setCollapsibleAtom])

  return { config, updateConfig, resetConfig }
}
